import GenericModal from "components/modal/GenericModal";
import styles from "css/modal/EditTagsModal.module.css";
import InputWithLabel from "components/input/InputWithLabel";
import InputLabel from "components/input/InputLabel";
import { useState } from "react";
import ButtonWithText from "components/buttons/ButtonWithText";
import ButtonTheme from "types/enums/ButtonTheme";
import FontClass from "types/enums/FontClass";
import ReactTagInput from "@pathofdev/react-tag-input";
import graphql from "babel-plugin-relay/macro";
import { useFragment, useMutation } from "react-relay";
import { EditTagsModal_MetadataAccount$key } from "components/modal/__generated__/EditTagsModal_MetadataAccount.graphql";
import { EditTagsModalMutation } from "components/modal/__generated__/EditTagsModalMutation.graphql";
import { notify } from "components/toast/notifications";
import logIfNotProd from "utils/logIfNotProd";

const mutation = graphql`
  mutation EditTagsModalMutation(
    $deleteFilter: NftToTag_bool_exp!
    $insertObjects: [NftToTag_insert_input!]!
  ) {
    delete_NftToTag(where: $deleteFilter) {
      returning {
        nftId
        tagId
      }
    }

    insert_NftToTag(objects: $insertObjects) {
      returning {
        nftId
        tagId
      }
    }
  }
`;

const fragment = graphql`
  fragment EditTagsModal_MetadataAccount on MetadataAccount {
    id
    mint

    tags
  }
`;

type Props = {
  isShown: boolean;
  metadataAccount: EditTagsModal_MetadataAccount$key;
  onHide: () => void;
};

export default function EditTagsModal({
  isShown,
  metadataAccount,
  onHide,
}: Props): JSX.Element {
  const [commit] = useMutation<EditTagsModalMutation>(mutation);
  const metadataAccountData = useFragment(fragment, metadataAccount);
  const originalTags = [...metadataAccountData.tags];
  const [tags, setTags] = useState<Array<string>>(originalTags);
  const [isLoading, setIsLoading] = useState(false);

  const tagsInput = (
    <InputWithLabel
      label={
        <InputLabel
          label="Tags"
          subLabel="You can add up to 10 tags describing your work to help collectors discover your art."
        />
      }
      input={
        <ReactTagInput
          maxTags={10}
          onChange={(newTags) => setTags(newTags)}
          removeOnBackspace
          tags={tags}
        />
      }
    />
  );

  return (
    <GenericModal isShown={isShown} onHide={onHide} title="Edit tags">
      <div className={styles.body}>
        {tagsInput}
        <ButtonWithText
          buttonTheme={ButtonTheme.PurpleGradient}
          className={styles.saveButton}
          fontClass={FontClass.NavLink}
          showLoadingSpinner={isLoading}
          onClick={() => {
            setIsLoading(true);
            const newTags = tags.filter((tag) => !originalTags.includes(tag));
            const deletedTags = originalTags.filter(
              (tag) => !tags.includes(tag)
            );

            commit({
              variables: {
                insertObjects: newTags.map((tag) => ({
                  nftId: metadataAccountData.mint as string,
                  Tag: {
                    data: { value: tag.trim() },
                    on_conflict: {
                      constraint: "Tag_value_key",
                      update_columns: ["value"],
                    },
                  },
                })),
                deleteFilter: {
                  _or: deletedTags.map((tag) => ({
                    Tag: { value: { _eq: tag } },
                  })),
                },
              },
              onCompleted: () => {
                setIsLoading(false);
                onHide();
              },
              onError: (e) => {
                logIfNotProd("Error saving tags", e);
                notify({
                  type: "error",
                  message: "Unexpected error saving tags",
                });
              },
              updater: (store) => {
                const record = store
                  .getRoot()
                  .getLinkedRecord("metadataAccountForMint", {
                    input: { mint: metadataAccountData.mint as string },
                  });
                if (record != null) {
                  record.setValue(tags, "tags");
                }
              },
            });
          }}
        >
          Save
        </ButtonWithText>
      </div>
    </GenericModal>
  );
}
